import type { ReactElement } from "react";

import { FormField, TextInput, Select } from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";
import * as Yup from "yup";

import type {
  CustomQuery,
  CustomQueryFormProps,
  CustomQueryViewProps,
} from "./custom-query";

type ReadMode = "object" | "lastModified" | "allObjects" | "newestSubfolder";

function getReadMode(query: Partial<CustomQuery> | undefined): ReadMode {
  if (query?.readNewestSubfolder) {
    return "newestSubfolder";
  }
  if (query?.readAllObjectsAtPath) {
    return "allObjects";
  }
  return query?.useLastModified ? "lastModified" : "object";
}

/**
 * @param [opts]
 * @param [opts.objectLabel="object"] What to call an object ("object", "S3 object", "file", ...)
 * @param [opts.enableUseLastModified=false] Whether to offer reading the last modified object
 * @param [opts.enableReadAllObjectsAtPath=false] Whether to offer reading all objects at the path
 */
export default function getUploadForm(opts?: {
  objectLabel?: string;
  enableUseLastModified?: boolean;
  enableReadAllObjectsAtPath?: boolean;
}) {
  const {
    objectLabel = "object",
    enableUseLastModified = false,
    enableReadAllObjectsAtPath = false,
  } = opts ?? {};

  const PATH_LABEL_EDIT = `Relative path to ${objectLabel} inside bucket`;
  const FOLDER_PATH_LABEL_EDIT = "Relative path to folder inside bucket";
  const PATH_LABEL_VIEW = "Object path";
  const FOLDER_PATH_LABEL_VIEW = "Folder path";
  const PATH_DESCRIPTION =
    `The ${objectLabel} must be a CSV file (with header row) or a JSON file. ` +
    `It may optionally be compressed with gzip or Brotli; the archive format is inferred from the file extension (.gz or .br).`;

  const READ_MODE_LABEL = "Read";
  const READ_MODES: Record<ReadMode, { label: string; description: string }> = {
    object: {
      label: `The ${objectLabel} at the path`,
      description: `Hightouch reads the ${objectLabel} at the given path.`,
    },
    lastModified: {
      label: `The last modified ${objectLabel} at the path`,
      description:
        `Hightouch reads the last modified ${objectLabel} at the given path. ` +
        "To search the entire bucket, leave the path empty.",
    },
    allObjects: {
      label: `All ${objectLabel}s at the path`,
      description:
        `Hightouch reads every ${objectLabel} directly in the path as one model, for data split into part files. ` +
        "Nested folders and names starting with _ or . are skipped.",
    },
    newestSubfolder: {
      label: `All ${objectLabel}s in the newest subfolder`,
      description:
        "For data delivered to a new folder each time, like 20260101/. " +
        `Hightouch reads every ${objectLabel} in the subfolder whose name sorts last, skipping subfolders with no ${objectLabel}s yet. ` +
        "Folder names must sort in date order.",
    },
  };

  const FIELD_SEPARATOR_LABEL_EDIT = "Custom separator (CSV only)";
  const FIELD_SEPARATOR_LABEL_VIEW = "CSV separator";
  const FIELD_SEPARATOR_DESCRIPTION = "Field separator for CSV files";

  const FILE_TYPE_LABEL = "File type";
  const FILE_TYPE_DESCRIPTION =
    "File type (optional, defaults to extension; if compressed using GZip or Brotli, the .gz or .br extension is mandatory)";
  const FILE_TYPE_OPTIONS = [
    { label: "Automatic", value: "automatic" },
    { label: "CSV", value: "csv" },
    { label: "JSON", value: "json" },
    { label: "NDJSON", value: "ndjson" },
    { label: "Parquet", value: "parquet" },
  ];

  // TODO(eric.lovett): `QueryForm` is defined inside `getUploadForm`. Every
  // caller runs at module scope, so each one gets a stable component, but the
  // rule can't verify that. Please consider moving it to module scope with the
  // upload options as props.
  // biome-ignore lint/nursery/noComponentHookFactories: see TODO above
  function QueryForm({
    query,
    onChange,
  }: Readonly<CustomQueryFormProps>): ReactElement<any, any> {
    const { sourceS3ReadAllObjectsAtPath } = useFlags();
    const showReadAllObjects =
      enableReadAllObjectsAtPath && sourceS3ReadAllObjectsAtPath;
    const isModeEnabled: Record<ReadMode, boolean> = {
      object: true,
      lastModified: enableUseLastModified,
      allObjects: showReadAllObjects,
      newestSubfolder: showReadAllObjects,
    };
    const readMode = getReadMode(query);
    const readModeOptions = (Object.keys(READ_MODES) as ReadMode[])
      .filter((mode) => isModeEnabled[mode] || mode === readMode)
      .map((mode) => ({ value: mode, label: READ_MODES[mode].label }));

    const handleChange = (state: Partial<CustomQuery>) => {
      onChange({
        // useLastModified must not be null if enableUseLastModified is true.
        // That can cause unexpected errors when attempting to edit the model
        useLastModified: enableUseLastModified
          ? (state.useLastModified ?? false)
          : undefined,
        ...state,
      });
    };

    return (
      <>
        <FormField
          description={PATH_DESCRIPTION}
          label={
            readMode === "object" ? PATH_LABEL_EDIT : FOLDER_PATH_LABEL_EDIT
          }
        >
          <TextInput
            placeholder={readMode === "object" ? "file.csv" : "folder/"}
            value={query?.path ?? ""}
            onChange={(event) =>
              handleChange({
                ...query,
                type: "string",
                path: event.target.value,
              })
            }
          />
        </FormField>
        {readModeOptions.length > 1 && (
          <FormField
            description={READ_MODES[readMode].description}
            label={READ_MODE_LABEL}
          >
            <Select
              options={readModeOptions}
              value={readMode}
              onChange={(mode) => {
                if (!mode) {
                  return;
                }
                handleChange({
                  ...query,
                  useLastModified: mode === "lastModified",
                  readAllObjectsAtPath: mode === "allObjects",
                  readNewestSubfolder: mode === "newestSubfolder",
                });
              }}
            />
          </FormField>
        )}
        <FormField description={FILE_TYPE_DESCRIPTION} label={FILE_TYPE_LABEL}>
          <Select
            options={FILE_TYPE_OPTIONS}
            placeholder="Automatic"
            value={query?.fileType ?? "automatic"}
            onChange={(value) =>
              handleChange({
                ...query,
                type: "string",
                fileType: value ?? "automatic",
              })
            }
          />
        </FormField>
        <FormField
          description={FIELD_SEPARATOR_DESCRIPTION}
          label={FIELD_SEPARATOR_LABEL_EDIT}
        >
          <TextInput
            placeholder=","
            value={query?.fieldSeparator ?? ""}
            onChange={(event) =>
              handleChange({
                ...query,
                type: "string",
                fieldSeparator: event.target.value,
              })
            }
          />
        </FormField>
      </>
    );
  }

  // TODO(eric.lovett): `QueryView` is defined inside `getUploadForm`. Every
  // caller runs at module scope, so each one gets a stable component, but the
  // rule can't verify that. Please consider moving it to module scope with the
  // upload options as props.
  // biome-ignore lint/nursery/noComponentHookFactories: see TODO above
  function QueryView(
    props: Readonly<CustomQueryViewProps>,
  ): ReactElement<any, any> {
    const readMode = getReadMode(props.query);
    return (
      <>
        <FormField
          label={
            readMode === "object" ? PATH_LABEL_VIEW : FOLDER_PATH_LABEL_VIEW
          }
        >
          {props.query.path}
        </FormField>
        <FormField label={FIELD_SEPARATOR_LABEL_VIEW}>
          {props.query.fieldSeparator || ","}
        </FormField>
        <FormField label={FILE_TYPE_LABEL}>
          {props.query.fileType?.toUpperCase() || "Automatic"}
        </FormField>
        {(enableUseLastModified || enableReadAllObjectsAtPath) && (
          <FormField label={READ_MODE_LABEL}>
            {READ_MODES[readMode].label}
          </FormField>
        )}
      </>
    );
  }

  const querySchema = Yup.lazy<CustomQuery | undefined>((_) => {
    if (enableUseLastModified) {
      return Yup.object().shape({
        type: Yup.string().required(),
        useLastModified: Yup.boolean().required(),
        // path is not required if useLastModified is true
        path: Yup.string().when("useLastModified", {
          is: true,
          then: Yup.string().notRequired(),
          else: Yup.string().required(),
        }),
      });
    }
    return Yup.object().shape({
      type: Yup.string().required(),
      path: Yup.string().required(),
    });
  });

  return { QueryForm, QueryView, querySchema };
}
